// ResiGen project view: the four inputs, the valuation breakdown, and the PREPAYMENT AMOUNT with a
// copy button (the value dealers paste into their own proposal software). No enrichments, no quotes.

import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { toastOnError } from '/imports/ui/utils';
import { useUser, useFindOne, useSubscribe, useTracker } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Accounts } from '/imports/api/accounts';
import { copyToClipboard, formatNumber } from '/imports/utils';
import { ResigenProjects, resigenDeleteProject, resigenItcStatusLabels } from '/imports/api/resigenProjects';
import { ResigenProjectModal, resigenCanAccess, resigenMoney } from '/imports/ui/ResiGenPage';

const ResiGenProjectViewPageStyled = styled(Page)`
  .heading-buttons { display: flex; gap: 0.5rem; align-items: center; }
  .resigen-meta {
    display: flex; flex-wrap: wrap; gap: 0.5rem 2.25rem;
    margin: 0.25rem 0 0.9rem; padding: 0.65rem 1rem;
    border: ${css.inputBorder}; border-radius: 0.5rem; background: ${css.grey8};
  }
  .resigen-meta .item { display: flex; flex-direction: column; gap: 0.1rem; min-width: 8rem; }
  .resigen-meta .k { color: ${css.grey4}; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .resigen-meta .v { color: ${css.grey2}; font-size: 0.9rem; font-weight: 600; }
  .breakdown { max-width: 34rem; }
  .breakdown table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  .breakdown td { padding: 0.4rem 0.6rem; border-bottom: 1px solid ${css.grey6}; }
  .breakdown td.num { text-align: right; font-weight: 600; }
  .breakdown .rate { color: ${css.grey4}; font-weight: normal; font-size: 0.8rem; }
  .prepayment {
    max-width: 34rem; margin-top: 1rem; padding: 1rem 1.25rem;
    border: 2px solid ${css.primaryColor}; border-radius: 0.5rem;
    display: flex; align-items: center; gap: 1.5rem;
  }
  .prepayment .k { color: ${css.grey3}; font-size: 0.9rem; }
  .prepayment .v { font-size: 2rem; font-weight: bold; color: ${css.primaryColor}; user-select: all; }
  .prepayment .copy { margin-left: auto; }
`;

/** ResiGen project view page (route: /resigen/:_id/view). */
export const ResiGenProjectViewPage = () => {
  const { _id } = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const loading = useSubscribe(Publications.resigenProjects);
  const project = useTracker(() => (_id ? ResigenProjects.findOne(_id) : undefined), [_id]);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  if (!resigenCanAccess(user, account)) return <NotPermittedPage />;
  if (loading()) return <ResiGenProjectViewPageStyled><PageBody><LoadingFlex /></PageBody></ResiGenProjectViewPageStyled>;
  if (!project) return <ResiGenProjectViewPageStyled><PageBody>{strings.NotFound()}</PageBody></ResiGenProjectViewPageStyled>;
  const calc = project.calc;
  const copyAmount = () => {
    copyToClipboard(String(Math.round(calc.prepaymentAmount)));
    toast.success(strings.CopiedToClipboard());
  };
  const doDelete = toastOnError(async () => {
    await resigenDeleteProject.call({ _id: project._id });
    navigate('/resigen');
  });
  return (
    <ResiGenProjectViewPageStyled>
      <PageHeading
        left={<h1>{project.name}</h1>}
        right={
          <div className="heading-buttons">
            <Button className="secondary" onClick={() => setShowEdit(true)}>{strings.Edit()}</Button>
            <Button className="secondary" onClick={() => setShowDelete(true)}>{strings.Delete()}</Button>
            <Link to="/resigen"><Button className="secondary">{strings.ResiGen()}</Button></Link>
          </div>
        }
      />
      <PageBody>
        <div className="resigen-meta">
          <div className="item"><span className="k">{strings.SolarSystemSizeKwDc()}</span><span className="v">{formatNumber(project.systemSizeKw, 2)}</span></div>
          <div className="item"><span className="k">{strings.EssCapacityKwh()}</span><span className="v">{formatNumber(project.essCapacityKwh, 2)}</span></div>
          <div className="item"><span className="k">{strings.EnergyCommunityAdder()}</span><span className="v">{project.energyCommunity ? strings.Yes() : strings.No()}</span></div>
          <div className="item"><span className="k">{strings.DisadvantagedCommunityAdder()}</span><span className="v">{project.disadvantagedCommunity ? strings.Yes() : strings.No()}</span></div>
          <div className="item"><span className="k">{strings.ItcStatus()}</span><span className="v">{resigenItcStatusLabels[calc.itcStatus]}</span></div>
        </div>
        <div className="breakdown">
          <table>
            <tbody>
              <tr>
                <td>{strings.SolarValuation()} <span className="rate">{formatNumber(project.systemSizeKw, 2)} kW × ${calc.solarRate}/W</span></td>
                <td className="num">{resigenMoney(calc.solarValue)}</td>
              </tr>
              <tr>
                <td>{strings.BatteryValuation()} <span className="rate">{formatNumber(project.essCapacityKwh, 2)} kWh × ${calc.batteryRate}/kWh</span></td>
                <td className="num">{resigenMoney(calc.batteryValue)}</td>
              </tr>
              <tr>
                <td>{strings.GrossProjectValue()}</td>
                <td className="num">{resigenMoney(calc.grossValue)}</td>
              </tr>
              <tr>
                <td>{strings.TaxEquityContribution()} <span className="rate">{formatNumber(calc.contributionPct * 100, 1)}%</span></td>
                <td className="num">−{resigenMoney(calc.contributionAmount)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="prepayment">
          <div>
            <div className="k">{strings.PrepaymentAmount()}</div>
            <div className="v" id="prepayment-amount">{resigenMoney(calc.prepaymentAmount)}</div>
          </div>
          <Button className="primary copy" onClick={() => copyAmount()}>{strings.Copy()}</Button>
        </div>
      </PageBody>
      {showEdit && <ResigenProjectModal existing={project} onClose={() => setShowEdit(false)} />}
      {
        showDelete &&
        <ConfirmModal
          title={strings.Delete()}
          body={strings.DeleteItemConfirm()}
          onConfirm={() => doDelete()}
          onClose={() => setShowDelete(false)}
        />
      }
    </ResiGenProjectViewPageStyled>
  );
};
